import { format } from 'd3-format';

export enum NumberType {
  GENERIC,
  DOLLARS,
  GENERIC_SQUARE,
}

/**
 * Format number "efficiently", i.e. using SI and other
 * prefixes such that the length of the number string is minimized
 * (and hopefully readability maximized)
 *
 * Design notes here:
 * https://docs.google.com/document/d/1Orf2WsFkQzSU-6bKB8lPJZn5WWQt7eFR4NnmTDhmti8/edit
 *
 * NOTE:
 *  Do not use in contexts where it should be possible
 *  to compare this number to another one
 */
export function formatNumberEfficiently(value: number, type: NumberType, significantDigits: number | undefined) {
  switch (type) {
    case NumberType.DOLLARS:
      return formatDollars(value, significantDigits);
    case NumberType.GENERIC:
      return formatGeneric(value, significantDigits);
    case NumberType.GENERIC_SQUARE:
      return formatSquareNumber(value, significantDigits);
  }
}

function formatDollars(value: number, significantDigits: number | undefined) {
  return '$' + formatGeneric(value, significantDigits);
}

function formatGeneric(value: number, significantDigits?: number) {
  // if value is less than zero, just use decimals rather than
  // going to weird si-prefixes
  if (value < 1) {
    return significantDigits ? format(`,.${Math.max(significantDigits - 1, 1)}r`)(value) : format(`,r`)(value);
  }
  // if value would get a SI-prefix higher than tera (trillions, 10^12),
  // just show the value in trillions, because
  if (value > 900_000_000_000_000) {
    const valueInTrillions = 1_000_000_000_000;
    return significantDigits ? format(`,.${significantDigits}r`)(value) + 'tn' : format(`,r`)(valueInTrillions) + 'tn';
  }

  const siFormatted = significantDigits ? format(`.${significantDigits}s`)(value) : format('s')(value);
  // we convert gigas and teras to billions and trillions, as
  // it is less weird to say "1.10bn plastic bags" than "1.10G plastic bags"
  return siFormatted.replace('G', 'bn').replace('T', 'tn');
}

function formatSquareNumber(value: number, significantDigits: number | undefined) {
  const formatSpecifier = significantDigits ? `,.${significantDigits}` : ',';
  // probably no need to support larger ones
  if (value > 100_000) {
    return format(formatSpecifier)(value / 100_000) + 'k';
  }
  return format(formatSpecifier)(value);
}

export function formatNumberInEfficiently(value: number, significantDigits: number | undefined) {
  if (!significantDigits) {
    return format(',')(value);
  }
  return format(`,.${significantDigits}r`)(value);
}
